import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createStaffMember,
  deleteStaffMember,
  fetchStaff,
  getApiErrorMessage,
  updateStaffMember,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";

const EMPTY_FORM = {
  full_name: "",
  telegram_username: "",
  phone_personal: "",
  email: "",
  email_1c: "",
  email_corporate: "",
  birth_date: "",
  inn: "",
  bank_account: "",
};

const STATUS_FILTERS = [
  { value: "all", label: "Все" },
  { value: "linked", label: "Подключены" },
  { value: "waiting", label: "Ждут /start" },
  { value: "survey_excluded", label: "Вне опроса" },
];

function emptyEditForm(employee) {
  return {
    full_name: employee.full_name,
    telegram_username: employee.telegram_username || "",
    telegram_id: employee.is_linked ? String(employee.telegram_id) : "",
    role: employee.role,
    phone_personal: employee.phone_personal || "",
    email: employee.email || "",
    email_1c: employee.email_1c || "",
    email_corporate: employee.email_corporate || "",
    birth_date: employee.birth_date || "",
    salary: employee.salary == null ? "" : String(employee.salary),
    inn: employee.inn || "",
    bank_account: employee.bank_account || "",
  };
}

function parseSalary(raw) {
  const trimmed = String(raw ?? "").trim().replace(/\s/g, "").replace(",", ".");
  if (!trimmed) return { ok: true, value: null };
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return { ok: false };
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return { ok: false };
  return { ok: true, value: num };
}

function formatBirthDate(value) {
  if (!value) return "—";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[3]}.${match[2]}.${match[1]}`;
}

function formatPhoneDisplay(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("7")) {
    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  }
  return phone || "";
}

function contactMetaParts(employee) {
  const parts = [];
  if (employee.phone_personal) parts.push(formatPhoneDisplay(employee.phone_personal));
  if (employee.email_corporate) parts.push(employee.email_corporate);
  return parts;
}

function ContactFields({ form, setForm, disabled, idPrefix }) {
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  return (
    <>
      <label className="login-label" htmlFor={`${idPrefix}-phone`}>
        Личный телефон
        <input
          id={`${idPrefix}-phone`}
          className="login-input"
          name="phone_personal"
          inputMode="tel"
          autoComplete="tel"
          value={form.phone_personal}
          onChange={set("phone_personal")}
          placeholder="79001234567"
          disabled={disabled}
        />
      </label>
      <label className="login-label" htmlFor={`${idPrefix}-email`}>
        Email
        <input
          id={`${idPrefix}-email`}
          className="login-input"
          name="email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={set("email")}
          disabled={disabled}
        />
      </label>
      <label className="login-label" htmlFor={`${idPrefix}-email-1c`}>
        1С
        <input
          id={`${idPrefix}-email-1c`}
          className="login-input"
          name="email_1c"
          type="email"
          value={form.email_1c}
          onChange={set("email_1c")}
          disabled={disabled}
        />
      </label>
      <label className="login-label" htmlFor={`${idPrefix}-email-corp`}>
        Корпоративная почта
        <input
          id={`${idPrefix}-email-corp`}
          className="login-input"
          name="email_corporate"
          type="email"
          value={form.email_corporate}
          onChange={set("email_corporate")}
          disabled={disabled}
        />
      </label>
      <label className="login-label" htmlFor={`${idPrefix}-inn`}>
        ИНН
        <input
          id={`${idPrefix}-inn`}
          className="login-input"
          name="inn"
          inputMode="numeric"
          autoComplete="off"
          aria-label="ИНН"
          value={form.inn}
          onChange={set("inn")}
          disabled={disabled}
        />
      </label>
      <label className="login-label" htmlFor={`${idPrefix}-bank-account`}>
        Счёт (20 цифр)
        <input
          id={`${idPrefix}-bank-account`}
          className="login-input"
          name="bank_account"
          inputMode="numeric"
          autoComplete="off"
          aria-label="Счёт (20 цифр)"
          value={form.bank_account}
          onChange={set("bank_account")}
          disabled={disabled}
        />
      </label>
    </>
  );
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function useModalFocus(active, containerRef) {
  useEffect(() => {
    if (!active || !containerRef.current) return undefined;
    const root = containerRef.current;
    const previouslyFocused = document.activeElement;
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusables = () => Array.from(root.querySelectorAll(selector));
    const first = focusables()[0];
    if (first) first.focus();

    const onKeyDown = (event) => {
      if (event.key !== "Tab") return;
      const list = focusables();
      if (list.length === 0) return;
      const idx = list.indexOf(document.activeElement);
      if (event.shiftKey) {
        if (idx <= 0) {
          event.preventDefault();
          list[list.length - 1].focus();
        }
      } else if (idx === list.length - 1) {
        event.preventDefault();
        list[0].focus();
      }
    };

    root.addEventListener("keydown", onKeyDown);
    return () => {
      root.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [active, containerRef]);
}

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionError, setActionError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const createModalRef = useRef(null);
  const editModalRef = useRef(null);
  const deleteModalRef = useRef(null);

  useModalFocus(createOpen, createModalRef);
  useModalFocus(Boolean(editTarget), editModalRef);
  useModalFocus(Boolean(deleteTarget), deleteModalRef);

  const loadEmployees = useCallback(async ({ soft = false } = {}) => {
    if (!soft) setLoading(true);
    setError("");
    try {
      const data = await fetchStaff();
      setEmployees(data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось загрузить сотрудников"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const summary = useMemo(() => {
    const waiting = employees.filter((e) => !e.is_linked).length;
    const surveyExcluded = employees.filter((e) => e.is_survey_excluded).length;
    const vacationExcluded = employees.filter((e) => e.is_vacation_excluded).length;
    return {
      total: employees.length,
      waiting,
      surveyExcluded,
      vacationExcluded,
    };
  }, [employees]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return employees.filter((e) => {
      if (statusFilter === "linked" && !e.is_linked) return false;
      if (statusFilter === "waiting" && e.is_linked) return false;
      if (statusFilter === "survey_excluded" && !e.is_survey_excluded) return false;
      if (!q) return true;
      const haystack = [
        e.full_name,
        e.telegram_username,
        e.phone_personal,
        e.email,
        e.email_1c,
        e.email_corporate,
        e.birth_date,
        formatBirthDate(e.birth_date),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [employees, query, statusFilter]);

  const closeCreate = () => {
    if (creating) return;
    setCreateOpen(false);
    setForm(EMPTY_FORM);
    setActionError("");
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    setActionError("");
    try {
      await createStaffMember({
        full_name: form.full_name.trim(),
        telegram_username: form.telegram_username.trim(),
        phone_personal: form.phone_personal.trim() || null,
        email: form.email.trim() || null,
        email_1c: form.email_1c.trim() || null,
        email_corporate: form.email_corporate.trim() || null,
        birth_date: form.birth_date || null,
        inn: form.inn.trim() || null,
        bank_account: form.bank_account.trim() || null,
      });
      setForm(EMPTY_FORM);
      setCreateOpen(false);
      await loadEmployees({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось добавить сотрудника"));
    } finally {
      setCreating(false);
    }
  };

  const toggleExclusion = async (employee, field) => {
    setActionError("");
    setBusyId(employee.id);
    try {
      await updateStaffMember(employee.id, { [field]: !employee[field] });
      await loadEmployees({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось изменить настройки сотрудника"));
    } finally {
      setBusyId(null);
    }
  };

  const openEdit = (employee) => {
    setEditTarget(employee);
    setEditForm(emptyEditForm(employee));
    setActionError("");
  };

  const closeEdit = () => {
    if (saving) return;
    setEditTarget(null);
  };

  const handleEditSave = async (event) => {
    event.preventDefault();
    if (!editTarget) return;

    const fullName = editForm.full_name.trim();
    const username = editForm.telegram_username.trim();
    const telegramIdRaw = editForm.telegram_id.trim();

    if (!fullName) {
      setActionError("Имя не может быть пустым");
      return;
    }
    if (!username) {
      setActionError("Telegram username не может быть пустым");
      return;
    }
    const salaryParsed = parseSalary(editForm.salary);
    if (!salaryParsed.ok) {
      setActionError("Зарплата должна быть числом не меньше 0");
      return;
    }
    let telegramId;
    if (telegramIdRaw) {
      telegramId = Number(telegramIdRaw);
      if (!Number.isInteger(telegramId) || telegramId <= 0) {
        setActionError("Telegram ID должен быть положительным целым числом");
        return;
      }
    }

    setSaving(true);
    setActionError("");
    try {
      await updateStaffMember(editTarget.id, {
        full_name: fullName,
        telegram_username: username,
        role: editForm.role,
        phone_personal: editForm.phone_personal.trim() || null,
        email: editForm.email.trim() || null,
        email_1c: editForm.email_1c.trim() || null,
        email_corporate: editForm.email_corporate.trim() || null,
        birth_date: editForm.birth_date || null,
        salary: salaryParsed.value,
        inn: editForm.inn.trim() || null,
        bank_account: editForm.bank_account.trim() || null,
        ...(telegramId ? { telegram_id: telegramId } : {}),
      });
      setEditTarget(null);
      await loadEmployees({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить изменения"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError("");
    try {
      await deleteStaffMember(deleteTarget.id);
      setDeleteTarget(null);
      await loadEmployees({ soft: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось удалить сотрудника"));
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (!deleteTarget && !editTarget && !createOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (deleting || saving || creating) return;
      setDeleteTarget(null);
      setEditTarget(null);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setActionError("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteTarget, editTarget, createOpen, deleting, saving, creating]);

  return (
    <div className="page-container staff-page">
      <div className="page-header staff-header">
        <div>
          <h2>Сотрудники</h2>
          <p className="page-subtitle staff-subtitle">
            Участники еженедельного опроса в Telegram — добавление, исключение из
            опроса/отпусков, удаление
          </p>
        </div>
        <div className="staff-header-actions">
          <button
            type="button"
            className="staff-add-btn"
            onClick={() => {
              setActionError("");
              setForm(EMPTY_FORM);
              setCreateOpen(true);
            }}
          >
            Добавить
          </button>
        </div>
      </div>

      {error && <ErrorRetry message={error} onRetry={loadEmployees} />}
      {actionError && !deleteTarget && !editTarget && !createOpen && (
        <div className="login-error" role="alert">
          {actionError}
        </div>
      )}

      {!error && (
        <section className="staff-metrics" aria-label="Сводка по сотрудникам">
          <div className="staff-metric">
            <span>Всего</span>
            <strong>{summary.total}</strong>
          </div>
          <div className="staff-metric">
            <span>Ждут /start</span>
            <strong>{summary.waiting}</strong>
          </div>
          <div className="staff-metric">
            <span>Вне опроса</span>
            <strong>{summary.surveyExcluded}</strong>
          </div>
          <div className="staff-metric">
            <span>Вне отпусков</span>
            <strong>{summary.vacationExcluded}</strong>
          </div>
        </section>
      )}

      <section className="staff-table-section">
        <div className="staff-toolbar">
          <label className="staff-search">
            <span className="staff-visually-hidden">Поиск сотрудников</span>
            <input
              type="search"
              name="staff-search"
              autoComplete="off"
              placeholder="Имя, username, телефон, email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Поиск по имени, username, телефону или email"
            />
          </label>
          <label className="staff-filter">
            <span>Статус</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Фильтр статуса"
            >
              {STATUS_FILTERS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <span className="staff-count" aria-live="polite">
            Показано {filtered.length} из {employees.length}
          </span>
        </div>

        {loading ? (
          <p className="settings-hint">Загрузка…</p>
        ) : filtered.length === 0 ? (
          <div className="staff-empty">
            <p className="staff-empty-title">
              {employees.length === 0 ? "Список сотрудников пуст" : "Ничего не найдено"}
            </p>
            <p className="staff-empty-text">
              {employees.length === 0
                ? "Добавьте первого участника еженедельного опроса."
                : "Измените поиск или фильтр статуса."}
            </p>
            {employees.length === 0 && (
              <button
                type="button"
                className="staff-add-btn"
                onClick={() => {
                  setActionError("");
                  setCreateOpen(true);
                }}
              >
                Добавить
              </button>
            )}
          </div>
        ) : (
          <div className="staff-table-wrap">
            <table className="staff-table">
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Username</th>
                  <th>День рождения</th>
                  <th>Статус</th>
                  <th>В опросе</th>
                  <th>В отпусках</th>
                  <th>Ежедневный отчёт</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((employee) => {
                  const inSurvey = !employee.is_survey_excluded;
                  const inVacation = !employee.is_vacation_excluded;
                  const dailyReportOn =
                    employee.is_manager || Boolean(employee.daily_report_enabled);
                  const busy = busyId === employee.id;
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div className="employee-cell">
                          <div className="employee-avatar" aria-hidden="true">
                            {initials(employee.full_name)}
                          </div>
                          <div className="employee-info">
                            <div className="employee-name">{employee.full_name}</div>
                            {contactMetaParts(employee).length > 0 && (
                              <div className="employee-meta">
                                {contactMetaParts(employee).join(" · ")}
                              </div>
                            )}
                            <div className="staff-badges">
                              {employee.role === "admin" && (
                                <span className="cd-badge cd-badge--accent">Админ бота</span>
                              )}
                              {employee.is_manager && (
                                <span className="cd-badge cd-badge--accent">Руководитель</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="staff-username">
                        {employee.telegram_username ? `@${employee.telegram_username}` : "—"}
                      </td>
                      <td>{formatBirthDate(employee.birth_date)}</td>
                      <td>
                        {employee.is_linked ? (
                          <span className="cd-badge cd-badge--yes">Подключён</span>
                        ) : (
                          <span
                            className="cd-badge cd-badge--locked"
                            title="Сотрудник ещё не запускал /start в боте"
                          >
                            Ждёт /start
                          </span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`staff-switch ${inSurvey ? "staff-switch--on" : ""}`}
                          aria-pressed={inSurvey}
                          aria-label={
                            inSurvey
                              ? `${employee.full_name}: участвует в опросе`
                              : `${employee.full_name}: исключён из опроса`
                          }
                          disabled={busy}
                          onClick={() => toggleExclusion(employee, "is_survey_excluded")}
                        >
                          <span className="staff-switch-track" aria-hidden="true" />
                          <span className="staff-switch-label">
                            {inSurvey ? "Да" : "Нет"}
                          </span>
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`staff-switch ${inVacation ? "staff-switch--on" : ""}`}
                          aria-pressed={inVacation}
                          aria-label={
                            inVacation
                              ? `${employee.full_name}: участвует в отпусках`
                              : `${employee.full_name}: исключён из отпусков`
                          }
                          disabled={busy}
                          onClick={() => toggleExclusion(employee, "is_vacation_excluded")}
                        >
                          <span className="staff-switch-track" aria-hidden="true" />
                          <span className="staff-switch-label">
                            {inVacation ? "Да" : "Нет"}
                          </span>
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`staff-switch ${dailyReportOn ? "staff-switch--on" : ""}`}
                          aria-pressed={dailyReportOn}
                          aria-label={
                            dailyReportOn
                              ? `${employee.full_name}: ежедневный отчёт включён`
                              : `${employee.full_name}: ежедневный отчёт выключен`
                          }
                          disabled={busy || employee.is_manager}
                          title={
                            employee.is_manager
                              ? "У руководителя ежедневный отчёт всегда доступен"
                              : undefined
                          }
                          onClick={() =>
                            toggleExclusion(employee, "daily_report_enabled")
                          }
                        >
                          <span className="staff-switch-track" aria-hidden="true" />
                          <span className="staff-switch-label">
                            {dailyReportOn ? "Да" : "Нет"}
                          </span>
                        </button>
                      </td>
                      <td className="staff-actions">
                        <button
                          type="button"
                          className="users-action-btn"
                          onClick={() => openEdit(employee)}
                        >
                          Редактировать
                        </button>
                        <button
                          type="button"
                          className="users-action-btn"
                          onClick={() => {
                            setDeleteTarget(employee);
                            setActionError("");
                          }}
                        >
                          Удалить
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {createOpen && (
        <div
          className="users-modal-backdrop staff-modal-backdrop"
          onClick={closeCreate}
        >
          <div
            ref={createModalRef}
            className="users-modal staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-create-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="staff-create-title">Новый сотрудник</h3>
            {actionError && (
              <div className="login-error" role="alert">
                {actionError}
              </div>
            )}
            <form className="users-form-grid" onSubmit={handleCreate}>
              <label className="login-label">
                Имя
                <input
                  className="login-input"
                  name="full_name"
                  autoComplete="name"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Иван Иванов"
                  required
                  disabled={creating}
                />
              </label>
              <label className="login-label">
                Telegram username
                <input
                  className="login-input"
                  name="telegram_username"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.telegram_username}
                  onChange={(e) => setForm({ ...form, telegram_username: e.target.value })}
                  placeholder="ivan_ivanov"
                  required
                  disabled={creating}
                />
              </label>
              <label className="login-label" htmlFor="staff-create-birth_date">
                День рождения
                <input
                  id="staff-create-birth_date"
                  className="login-input"
                  name="birth_date"
                  type="date"
                  value={form.birth_date}
                  onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                  disabled={creating}
                />
              </label>
              <ContactFields
                form={form}
                setForm={setForm}
                disabled={creating}
                idPrefix="staff-create"
              />
              <p className="settings-hint" style={{ gridColumn: "1 / -1", margin: 0 }}>
                Сотрудник появится в опросах сразу. Как только он напишет боту{" "}
                <code>/start</code>, его Telegram ID подставится автоматически.
              </p>
              <div className="users-form-actions">
                <button
                  type="button"
                  className="users-action-btn"
                  onClick={closeCreate}
                  disabled={creating}
                >
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={creating}>
                  {creating ? "Добавление…" : "Добавить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="users-modal-backdrop staff-modal-backdrop" onClick={closeEdit}>
          <div
            ref={editModalRef}
            className="users-modal client-edit-modal staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-edit-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="staff-edit-title">Редактирование: {editTarget.full_name}</h3>
            {actionError && (
              <div className="login-error" role="alert">
                {actionError}
              </div>
            )}
            <form className="users-form-grid" onSubmit={handleEditSave}>
              <label className="login-label">
                Имя
                <input
                  className="login-input"
                  name="full_name"
                  autoComplete="name"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  required
                  disabled={saving}
                />
              </label>
              <label className="login-label">
                Telegram username
                <input
                  className="login-input"
                  name="telegram_username"
                  autoComplete="off"
                  spellCheck={false}
                  value={editForm.telegram_username}
                  onChange={(e) =>
                    setEditForm({ ...editForm, telegram_username: e.target.value })
                  }
                  required
                  disabled={saving}
                />
              </label>
              <label className="login-label">
                Telegram ID (вручную)
                <input
                  className="login-input"
                  type="number"
                  name="telegram_id"
                  inputMode="numeric"
                  autoComplete="off"
                  min="1"
                  value={editForm.telegram_id}
                  onChange={(e) => setEditForm({ ...editForm, telegram_id: e.target.value })}
                  placeholder={
                    editTarget.is_linked ? String(editTarget.telegram_id) : "ждёт /start…"
                  }
                  disabled={saving}
                />
              </label>
              <label className="login-label">
                Роль в боте
                <select
                  className="login-input"
                  name="role"
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  disabled={saving}
                >
                  <option value="employee">Сотрудник</option>
                  <option value="admin">Админ бота</option>
                </select>
              </label>
              <label className="login-label" htmlFor="staff-edit-birth_date">
                День рождения
                <input
                  id="staff-edit-birth_date"
                  className="login-input"
                  name="birth_date"
                  type="date"
                  value={editForm.birth_date}
                  onChange={(e) => setEditForm({ ...editForm, birth_date: e.target.value })}
                  disabled={saving}
                />
              </label>
              <ContactFields
                form={editForm}
                setForm={setEditForm}
                disabled={saving}
                idPrefix="staff-edit"
              />
              <label className="login-label" htmlFor="staff-edit-salary">
                Зарплата,{"\u00A0"}₽
                <input
                  id="staff-edit-salary"
                  className="login-input"
                  name="salary"
                  autoComplete="off"
                  inputMode="decimal"
                  value={editForm.salary}
                  onChange={(e) => setEditForm({ ...editForm, salary: e.target.value })}
                  disabled={saving}
                />
              </label>
              <p className="settings-hint" style={{ gridColumn: "1 / -1", margin: 0 }}>
                Обычно Telegram ID подставляется сам, когда сотрудник пишет боту{" "}
                <code>/start</code>. Указывайте вручную только если точно знаете ID.
              </p>
              <div className="users-form-actions">
                <button
                  type="button"
                  className="users-action-btn"
                  onClick={closeEdit}
                  disabled={saving}
                >
                  Отмена
                </button>
                <button type="submit" className="login-button" disabled={saving}>
                  {saving ? "Сохранение…" : "Сохранить"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="users-modal-backdrop staff-modal-backdrop"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            ref={deleteModalRef}
            className="users-modal staff-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="staff-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="staff-delete-title">Удалить сотрудника?</h3>
            <p className="settings-hint">
              «{deleteTarget.full_name}» будет удалён без возможности восстановления
              {deleteTarget.responses_count > 0
                ? `, вместе с историей опросов (${deleteTarget.responses_count} ответов)`
                : ""}
              .
            </p>
            {actionError && (
              <div className="login-error" style={{ marginTop: 12 }} role="alert">
                {actionError}
              </div>
            )}
            <div className="users-modal-actions">
              <button
                type="button"
                className="users-action-btn"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Удаление…" : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
